import type { AppTokens } from './tokens';

/**
 * Minimum contrast of a raised divider against `surfaceRaised`: a decorative hairline (TMMAPP-0097
 * B2; the prototype's divider is about 1.3:1 in both modes). Below this the line disappears.
 */
const MIN_CONTRAST = 1.25;

function channel(value: number): number {
  const s = value / 255;
  return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}

function luminance(color: string): number | null {
  const hex = color.trim();
  const match = /^#([\da-f]{3}|[\da-f]{6})$/i.exec(hex);
  if (match === null) return null;
  const body = match[1] ?? '';
  const full = body.length === 3 ? [...body].map((c) => c + c).join('') : body;
  const r = Number.parseInt(full.slice(0, 2), 16);
  const g = Number.parseInt(full.slice(2, 4), 16);
  const b = Number.parseInt(full.slice(4, 6), 16);
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

function contrast(a: string, b: string): number {
  const left = luminance(a);
  const right = luminance(b);
  if (left === null || right === null) return 0;
  const lighter = Math.max(left, right);
  const darker = Math.min(left, right);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Divider and skeleton colour on `surfaceRaised`. The first of `line`, `surface2`,
 * and `lineStrong` that reaches 1.25:1 against the raised fill; otherwise `lineStrong`.
 */
export function raisedLine(
  tokens: Pick<AppTokens, 'line' | 'surface2' | 'lineStrong' | 'surfaceRaised'>,
): string {
  return (
    [tokens.line, tokens.surface2, tokens.lineStrong].find(
      (color) => contrast(color, tokens.surfaceRaised) >= MIN_CONTRAST,
    ) ?? tokens.lineStrong
  );
}
